const CHAT_API_URL = import.meta.env.VITE_CHAT_API_URL ?? 'http://localhost:8787'

export interface ChatMessage {
	role: 'user' | 'assistant'
	content: string
}

export class RateLimitError extends Error {
	constructor() {
		super('Rate limit exceeded')
		this.name = 'RateLimitError'
	}
}

/**
 * Posts messages to the cv-chat worker and yields response tokens as they stream in.
 * Parses the SSE stream from the worker (`data: {"response":"…"}\n\n`).
 * Throws RateLimitError on 429, Error on other failures.
 */
export async function* streamChat(
	messages: ChatMessage[],
	signal?: AbortSignal,
): AsyncGenerator<string, void, unknown> {
	const response = await fetch(`${CHAT_API_URL}/api/chat`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ messages }),
		signal,
	})

	if (response.status === 429) throw new RateLimitError()

	if (!response.ok) {
		let msg = `HTTP ${response.status}`
		try {
			const body = (await response.json()) as { error?: string }
			if (body.error) msg = body.error
		} catch { /* ignore */ }
		throw new Error(msg)
	}

	if (!response.body) throw new Error('No response body')

	const reader = response.body.getReader()
	const decoder = new TextDecoder()
	let buffer = ''

	try {
		for (;;) {
			const { done, value } = await reader.read()
			if (done) break

			buffer += decoder.decode(value, { stream: true })
			const lines = buffer.split('\n')
			buffer = lines.pop() ?? ''

			for (const line of lines) {
				if (!line.startsWith('data: ')) continue
				const data = line.slice(6).trim()
				if (data === '[DONE]') return
				try {
					const parsed = JSON.parse(data) as { response?: string }
					if (parsed.response) yield parsed.response
				} catch { /* malformed SSE line — skip */ }
			}
		}
	} finally {
		reader.releaseLock()
	}
}
